/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Partner API Service
 * CRUD endpoints for /api/v1/venta/partners
 *
 * The unified Partner entity replaces the separate Customer + Supplier entities.
 */

import { get, post, put, del } from '@/services/api/client';
import type { ApiResponse } from '@/services/api/types';
import type {
  Partner,
  PartnerDetail,
  PartnerSummary,
  PartnerStats,
  PartnerFilter,
  PartnerFinancialSummary,
  CreatePartnerRequest,
  UpdatePartnerRequest,
  CreditSettings,
} from '../types/partner';
import type { PagedResult } from '../types/product';

const BASE = '/venta/partners';

export async function getPartners(filter: PartnerFilter = {}): Promise<PagedResult<Partner>> {
  const queryParams: Record<string, unknown> = {};
  if (filter.search) queryParams.search = filter.search;
  if (filter.role) queryParams.role = filter.role;
  if (filter.active !== undefined) queryParams.active = filter.active;
  if (filter.page !== undefined) queryParams.page = filter.page;
  if (filter.size !== undefined) queryParams.size = filter.size;

  const response = await get<ApiResponse<PagedResult<Partner>>>(BASE, queryParams);
  return response.data;
}

export async function getPartnerById(id: string): Promise<PartnerDetail> {
  const response = await get<ApiResponse<PartnerDetail>>(`${BASE}/${id}`);
  return response.data;
}

export async function createPartner(data: CreatePartnerRequest): Promise<Partner> {
  const response = await post<ApiResponse<Partner>>(BASE, data);
  return response.data;
}

export async function updatePartner(id: string, data: UpdatePartnerRequest): Promise<Partner> {
  const response = await put<ApiResponse<Partner>>(`${BASE}/${id}`, data);
  return response.data;
}

export async function deletePartner(id: string): Promise<void> {
  await del<ApiResponse<void>>(`${BASE}/${id}`);
}

export async function getActivePartners(): Promise<PartnerSummary[]> {
  // Backend has no generic /active — merge buyers + sellers, deduplicate
  const [buyers, sellers] = await Promise.all([getActiveBuyers(), getActiveSellers()]);
  const map = new Map<string, PartnerSummary>();
  buyers.forEach((p) => map.set(p.id, p));
  sellers.forEach((p) => { if (!map.has(p.id)) map.set(p.id, p); });
  return Array.from(map.values());
}

export async function getActiveBuyers(): Promise<PartnerSummary[]> {
  const response = await get<ApiResponse<PartnerSummary[]>>(`${BASE}/active/buyers`);
  return response.data;
}

export async function getActiveSellers(): Promise<PartnerSummary[]> {
  const response = await get<ApiResponse<PartnerSummary[]>>(`${BASE}/active/sellers`);
  return response.data;
}

export async function getActiveBrokers(): Promise<PartnerSummary[]> {
  const response = await get<ApiResponse<PartnerSummary[]>>(`${BASE}/active/brokers`);
  return response.data;
}

export async function getPartnerStats(): Promise<PartnerStats> {
  const response = await get<ApiResponse<PartnerStats>>(`${BASE}/stats`);
  return response.data;
}

export async function getPartnerFinancials(id: string): Promise<PartnerFinancialSummary> {
  const response = await get<ApiResponse<PartnerFinancialSummary>>(`${BASE}/${id}/financials`);
  return response.data;
}

export async function getCreditSettings(): Promise<CreditSettings> {
  const response = await get<ApiResponse<CreditSettings>>(`${BASE}/credit-settings`);
  return response.data;
}

export async function updateCreditSettings(body: CreditSettings): Promise<CreditSettings> {
  const response = await put<ApiResponse<CreditSettings>>(`${BASE}/credit-settings`, body);
  return response.data;
}

export const partnerService = {
  getPartners,
  getPartnerById,
  createPartner,
  updatePartner,
  deletePartner,
  getActivePartners,
  getActiveBuyers,
  getActiveSellers,
  getActiveBrokers,
  getPartnerStats,
  getPartnerFinancials,
  getCreditSettings,
  updateCreditSettings,
};
